import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Spinner, Modal } from 'react-bootstrap';
import { BsPencil, BsTrash, BsThreeDotsVertical } from 'react-icons/bs';
import QuillEditor from '../components/QuillEditor';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import cmsPageService from '../api/cmsPageService';
import { useSelector } from 'react-redux';

const STATUT_OPTIONS = [
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'publie', label: 'Publié' },
  { value: 'archive', label: 'Archivé' },
];
const LANGUE_OPTIONS = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];
const EMPTY_FORM = { titre: '', /* slug: '', */ contenu: '', statut: 'brouillon', langue: 'fr', ordre: 0 };
const PAGE_CONTENT_WORD_LIMIT = 500;

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map(e => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function htmlToText(html = '') {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}
function countWords(html = '') {
  const text = htmlToText(html).trim();
  return text ? text.split(/\s+/).length : 0;
}
const statutStyle = v => ({
  padding: '2px 8px', borderRadius: 10, fontSize: 11,
  backgroundColor: v === 'publie' ? '#d4edda' : v === 'brouillon' ? '#fff3cd' : '#e2e3e5',
  color: v === 'publie' ? '#155724' : v === 'brouillon' ? '#856404' : '#383d41',
});

export default function PagesPage() {
  const userRole = useSelector((s) => s.auth.user?.role);
  const isViewer = userRole === 'viewer';
  const isAdmin = useSelector((s) => s.auth.user?.role) === 'admin';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [filters, setFilters] = useState({ statut: '', langue: '', search: '', page: 1, limit: 20 });
  const contentWordCount = countWords(form.contenu);
  const contentWordsRemaining = Math.max(PAGE_CONTENT_WORD_LIMIT - contentWordCount, 0);

  const handleClearFilters = () => {
    setFilters({ statut: '', langue: '', search: '', page: 1, limit: 20 });
  };

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.statut) params.statut = filters.statut;
      if (filters.langue) params.langue = filters.langue;
      if (filters.search) params.search = filters.search;
      const { data: res } = await cmsPageService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = e =>
    setFilters(f => ({ ...f, [e.target.name]: e.target.value, page: 1 }));

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setFormErrors(fe => ({ ...fe, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.titre) errors.titre = 'Titre requis';
    if (contentWordCount > PAGE_CONTENT_WORD_LIMIT) errors.contenu = `Le contenu ne doit pas dépasser ${PAGE_CONTENT_WORD_LIMIT} mots`;
    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    setSaving(true);
    try {
      const payload = {
        titre: form.titre,
        /* slug: form.slug || undefined, */
        contenu: form.contenu,
        statut: form.statut,
        langue: form.langue,
        ordre: parseInt(form.ordre, 10) || 0,
      };
      if (view === 'add') {
        await cmsPageService.create(payload);
        showToast('Page créée avec succès');
      } else {
        await cmsPageService.update(editItem.id, payload);
        showToast('Page mise à jour avec succès');
      }
      setView('list'); setForm(EMPTY_FORM); setEditItem(null); setFormErrors({});
      fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setSaving(false); }
  };

  const handleEditOpen = row => {
    setForm({ titre: row.titre, /* slug: row.slug || '', */ contenu: row.contenu || '', statut: row.statut, langue: row.langue || 'fr', ordre: row.ordre || 0 });
    setEditItem(row); setFormErrors({}); setView('edit');
  };

  const handleDelete = async () => {
    try {
      await cmsPageService.remove(deleteTarget.id);
      showToast('Page supprimée avec succès');
      setDeleteTarget(null); fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); setDeleteTarget(null); }
  };

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>PAGES DU SITE</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen(v => !v)}>
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres</span>
          {filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row>
            <Col md={10}><Row className="gy-1 gx-3">
              <Col md={3} xs={12}>
                <Form.Label className="small mb-0 fw-semibold">Statut</Form.Label>
                <Form.Select name="statut" value={filters.statut} onChange={handleFilterChange} size="sm">
                  <option value="">Tous</option>
                  {STATUT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={3} xs={12}>
                <Form.Label className="small mb-0 fw-semibold">Langue</Form.Label>
                <Form.Select name="langue" value={filters.langue} onChange={handleFilterChange} size="sm">
                  <option value="">Toutes</option>
                  {LANGUE_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={4} xs={12}>
                <Form.Label className="small mb-0 fw-semibo ld">Recherche</Form.Label>
                <Form.Control size="sm" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Titre..." />
              </Col>
            </Row></Col>
            <Col md={2} className="d-flex flex-wrap align-items-end justify-content-md-end justify-content-center gap-2 mt-2 mt-md-0">
              {['statut', 'langue', 'search'].some((k) => filters[k]) && (
                <Button onClick={handleClearFilters} variant="outline-secondary" size="sm" className="text-uppercase px-2">EFFACER</Button>
              )}
              {/* <Button onClick={fetchData} size="sm" style={{ backgroundColor: '#8B0000', border: 'none', letterSpacing: 1 }} className="text-uppercase px-2">RECHERCHER</Button> */}
            </Col>
          </Row>
        </div>
      </div>

      {/* Add/Edit Form */}
      {(view === 'add' || view === 'edit') && (
        <div style={{ maxWidth: 760, margin: '0 auto 24px' }}>
          <h6 className="fw-bold mb-2">{view === 'add' ? 'Nouvelle page' : 'Modifier la page'}</h6>
          <Form className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={3} className="fw-semibold" style={{ fontSize: 14 }}>Titre :</Form.Label>
              <Col sm={9}>
                <Form.Control name="titre" value={form.titre} onChange={handleFormChange} placeholder="À propos de nous" isInvalid={!!formErrors.titre} />
                <Form.Control.Feedback type="invalid">{formErrors.titre}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            {/* Slug field - commented out */}
            {/* <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={3} className="fw-semibold" style={{ fontSize: 14 }}>Slug (URL) :</Form.Label>
              <Col sm={9}>
                <Form.Control name="slug" value={form.slug} onChange={handleFormChange} placeholder="a-propos (auto-généré si vide)" />
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>Exemple: a-propos → /pages/a-propos</Form.Text>
              </Col>
            </Form.Group> */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className="fw-semibold" style={{ fontSize: 14 }}>Contenu :</Form.Label>
              <Col sm={9}>
                <QuillEditor
                  value={form.contenu}
                  onChange={val => {
                    setForm(f => ({ ...f, contenu: val }));
                    setFormErrors(fe => ({ ...fe, contenu: '' }));
                  }}
                  placeholder="Contenu de la page…"
                  error={!!formErrors.contenu}
                  maxWords={PAGE_CONTENT_WORD_LIMIT}
                  onLimitExceeded={() => {
                    setFormErrors(fe => ({ ...fe, contenu: `Limite atteinte : le contenu ne doit pas dépasser ${PAGE_CONTENT_WORD_LIMIT} mots.` }));
                  }}
                />
                <div className="d-flex justify-content-between align-items-start mt-1" style={{ gap: 12 }}>
                  <div className="text-danger" style={{ fontSize: 12 }}>
                    {formErrors.contenu}
                  </div>
                  <div
                    className={contentWordCount > PAGE_CONTENT_WORD_LIMIT ? 'text-danger' : 'text-muted'}
                    style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                  >
                    {contentWordCount} mots saisis / {contentWordsRemaining} restants
                  </div>
                </div>
              </Col>
            </Form.Group>
            <Row className="mb-3">
              <Col sm={3} />
              <Col sm={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>Statut :</Form.Label>
                <Form.Select name="statut" value={form.statut} onChange={handleFormChange}>
                  {STATUT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col sm={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>Langue :</Form.Label>
                <Form.Select name="langue" value={form.langue} onChange={handleFormChange}>
                  {LANGUE_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Form.Select>
              </Col>
              <Col sm={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>Ordre :</Form.Label>
                <Form.Control type="number" name="ordre" value={form.ordre} onChange={handleFormChange} min={0} />
              </Col>
            </Row>
            <div className="text-center mt-3 d-flex flex-wrap gap-3 justify-content-center">
              <Button size="sm" variant="outline-secondary" onClick={() => { setView('list'); setForm(EMPTY_FORM); setFormErrors({}); }} style={{ letterSpacing: 1, paddingInline: 24 }}>ANNULER</Button>
              <Button className='text-nowrap' size="sm" disabled={saving} onClick={handleSave} style={{ backgroundColor: '#8B0000', border: 'none', letterSpacing: 1, paddingInline: 40 }}>
                {saving ? <Spinner size="sm" animation="border" /> : view === 'add' ? 'ENREGISTRER' : 'ENREGISTRER LES MODIFICATIONS'}
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* List */}
      {view === 'list' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">Résultats ({pagination.total ?? data.length})</h6>
            {isViewer ?
              <></>
              :
              <Button size="sm" style={{ backgroundColor: '#8B0000', border: 'none' }} onClick={() => { setForm(EMPTY_FORM); setFormErrors({}); setView('add'); }}>+ Nouvelle Page</Button>
            }
          </div>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8B0000' }} /></div>
          ) : data.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucune page trouvée.</div>
          ) : (
            <>
              <div className="table-mcm-wrap d-none d-md-block" style={{ overflowX: 'auto' }}>
                <table className="table-mcm">
                  <thead><tr>
                    {[
                      'Ordre',
                      'Titre',
                      /* 'Slug', */
                      'Statut',
                      'Langue',
                      'Modifié le',
                      !isViewer && 'Actions',
                    ]
                      .filter(Boolean)
                      .map((h) => (
                        <th key={h} className="th-mcm">
                          {h}
                        </th>
                      ))}
                  </tr></thead>
                  <tbody>
                    {data.map(row => (
                      <tr key={row.id}>
                        <td className="td-mcm" style={{ color: '#888' }}>{row.ordre}</td>
                        <td className="td-mcm" style={{ fontWeight: 600 }}>{row?.titre?.charAt(0)?.toUpperCase() + row?.titre?.slice(1)}</td>
                        {/* <td className="td-mcm"><code style={{ fontSize: 11, color: '#8B0000' }}>{row.slug}</code></td> */}
                        <td className="td-mcm"><span style={statutStyle(row.statut)}>{STATUT_OPTIONS.find(s => s.value === row.statut)?.label || row.statut}</span></td>
                        <td className="td-mcm">{row.langue?.toUpperCase()}</td>
                        <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.updated_at)}</td>
                        {isViewer ?
                          <></>
                          :
                          <td className="td-mcm">
                            <div className="d-flex gap-2">
                              <BsPencil size={15} style={{ cursor: 'pointer', color: '#555' }} onClick={() => handleEditOpen(row)} />
                              {isAdmin ? '' :
                                <BsTrash size={15} style={{ cursor: 'pointer', color: '#8B0000' }} onClick={() => setDeleteTarget(row)} />
                              }
                            </div>
                          </td>
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-md-none">
                <div className="d-flex flex-column gap-3">
                  {data.map(row => (
                    <div key={row.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                      <div className="d-flex justify-content-between border-bottom border-muted w-100 pb-3 align-items-start mb-3">
                        <div>
                          <div style={{ fontWeight: 600, color: '#8B0000' }}>{row.titre}</div>
                          <div className='text-muted fw-semibold' style={{ fontSize: 12 }}>
                            {formatDate(row.updated_at)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className='text-capitalize' style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11,
                            backgroundColor: row.statut === 'publie' ? '#d4edda' : row.statut === 'brouillon' ? '#fff3cd' : '#e2e3e5',
                            color: row.statut === 'publie' ? '#155724' : row.statut === 'brouillon' ? '#856404' : '#383d41',
                            whiteSpace: 'nowrap',
                          }}>
                            {STATUT_OPTIONS.find(s => s.value === row.statut)?.label || row.statut}
                          </span>
                          {isViewer ?
                            <></>
                            :
                            <div style={{ position: 'relative' }}>
                              <BsThreeDotsVertical
                                onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)}
                                size={18}
                                color="#555"
                                style={{ cursor: 'pointer' }}
                              />
                              {openMenuId === row.id && (
                                <div
                                  style={{
                                    position: 'absolute', right: 0, top: '100%', zIndex: 100,
                                    backgroundColor: '#fff', border: '1px solid #e0e0e0',
                                    borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    minWidth: 140, overflow: 'hidden', marginTop: 4,
                                  }}
                                >
                                  <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                                    onClick={() => { handleEditOpen(row); setOpenMenuId(null); }}
                                  >
                                    <BsPencil size={13} color="#555" /> Modifier
                                  </button>
                                  {isAdmin ? '' :
                                    <button
                                      type="button"
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8B0000', cursor: 'pointer' }}
                                      onClick={() => { setDeleteTarget(row); setOpenMenuId(null); }}
                                    >
                                      <BsTrash size={13} color="#8B0000" /> Supprimer
                                    </button>
                                  }
                                </div>
                              )}
                            </div>
                          }
                        </div>
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        {/* <div><span className="fw-semibold">Slug: </span><code style={{ fontSize: 12, color: '#8B0000' }}>{row.slug}</code></div> */}
                        <div><span className="fw-semibold">Langue: </span>{row.langue?.toUpperCase()}</div>
                        <div><span className="fw-semibold">Ordre: </span>{row.ordre}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          <Pagination
            currentPage={filters.page}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        </>
      )}

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          {/* <button onClick={() => setDeleteTarget(null)} className="modal-close-btn">×</button> */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cette page ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>
          {deleteTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-2"><span className="fw-semibold" style={{ fontSize: 14, color: '#8B0000' }}>{deleteTarget.titre}</span></div>
              {/* <div className="mb-2"><span className="fw-semibold">Slug : </span><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{deleteTarget.slug}</span></div> */}
              <div className="mb-2"><span className="fw-semibold">Langue : </span>{LANGUE_OPTIONS.find(l => l.value === deleteTarget.langue)?.label || deleteTarget.langue}</div>
              <div><span className="fw-semibold">Statut : </span>
                <span style={statutStyle(deleteTarget.statut)}>{STATUT_OPTIONS.find(s => s.value === deleteTarget.statut)?.label || deleteTarget.statut}</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>ANNULER</button>
            <button onClick={handleDelete} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>SUPPRIMER</button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
